'use client';

/**
 * 登录表单面板（2026-10-08）：登录页（LoginPage）与登录弹窗（LoginDialog）共享的表单核心。
 * 功能红线（勿删减）：手机号+验证码登录 / 60s 倒计时 / 协议勾选校验 /
 * 邀请码折叠 + ?aff= 预填 / 短信-密码双模式切换（按部署配置下发）/
 * 品牌定制 branding / 登录成功依赖 verifySmsCode 内部广播 auth:changed（本组件只触发 onSuccess 回调）。
 * 深色 purple 主题，与画布登录页保持一致。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Logo } from '@/components/Logo';
import {
  Phone, ShieldCheck, Loader2, AlertCircle, LogIn, User, Lock, Gift, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  sendSmsCode, verifySmsCode, ApiError,
  passwordLogin, getPublicConfig, type BrandingConfig,
} from '@/lib/api';
import { getBasePath } from '@/lib/utils';
import { isValidPhone, isVirtualOperatorPhone, VIRTUAL_OPERATOR_MESSAGE } from '@/lib/phoneUtils';

interface LoginFormPanelProps {
  /** 登录成功后的回调（弹窗：关闭+刷新；登录页：无需处理，auth:changed 由页面监听） */
  onSuccess?: () => void;
  /**
   * 视觉变体：
   * - page（默认）：画布登录页 purple 主题（品牌头 + 卡片容器）
   * - dialog：首页弹窗 emerald 主题（2026-10-08 用户定稿「之前的颜色和布局比较好看」——
   *   旧弹窗的深灰层次 + emerald 主按钮 + 简化标题头，无 logo 方块/无内层卡片）
   */
  variant?: 'page' | 'dialog';
}

export function LoginFormPanel({ onSuccess, variant = 'page' }: LoginFormPanelProps) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsVerifying, setSmsVerifying] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [basePath, setBasePath] = useState('');
  // 登录方式（部署形态开关）：sms=短信验证码 / password=账号密码
  const [authMode, setAuthMode] = useState<'sms' | 'password'>('sms');
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [branding, setBranding] = useState<BrandingConfig | null>(null);
  const [loginName, setLoginName] = useState('');
  const [password, setPassword] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  // 邀请活动（2026-09-18）：URL ?aff=CODE 预填邀请码，注册时绑定（仅全新账号生效）
  const [inviteCode, setInviteCode] = useState('');
  // 邀请码输入默认折叠（2026-09-18 用户要求）；aff 链接进入自动展开
  const [inviteOpen, setInviteOpen] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setBasePath(getBasePath());
    if (typeof window !== 'undefined') {
      const aff = new URLSearchParams(window.location.search).get('aff');
      if (aff && /^\d{6}$/.test(aff.trim())) { setInviteCode(aff.trim()); setInviteOpen(true); }
    }
    // 读取部署形态开关：私有化默认密码登录；同时加载品牌定制
    getPublicConfig().then((cfg) => {
      setSmsEnabled(cfg.smsAuthEnabled);
      setPasswordEnabled(cfg.passwordAuthEnabled);
      if (!cfg.smsAuthEnabled && cfg.passwordAuthEnabled) setAuthMode('password');
      setBranding(cfg.branding);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const startCountdown = useCallback(() => {
    setCountdown(60);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSendCode = useCallback(async () => {
    if (!isValidPhone(phone)) {
      setAuthError('请输入正确的手机号');
      return;
    }
    if (isVirtualOperatorPhone(phone)) {
      setAuthError(VIRTUAL_OPERATOR_MESSAGE);
      return;
    }
    setSmsSending(true);
    setAuthError(null);
    try {
      await sendSmsCode(phone);
      startCountdown();
    } catch (err) {
      setAuthError(err instanceof ApiError ? err.message : '发送失败');
    } finally {
      setSmsSending(false);
    }
  }, [phone, startCountdown]);

  const handleVerifyLogin = useCallback(async () => {
    if (!isValidPhone(phone)) {
      setAuthError('请输入正确的手机号');
      return;
    }
    if (code.length !== 6) {
      setAuthError('请输入6位验证码');
      return;
    }
    if (!hasAgreed) {
      setAuthError('请先阅读并同意用户协议和隐私政策');
      return;
    }
    setSmsVerifying(true);
    setAuthError(null);
    try {
      await verifySmsCode(phone, code, inviteCode || undefined);
      // verifySmsCode 内部已 dispatch auth:changed，无需重复触发
      onSuccess?.();
    } catch (err) {
      setAuthError(err instanceof ApiError ? err.message : '验证失败');
    } finally {
      setSmsVerifying(false);
    }
  }, [phone, code, hasAgreed, inviteCode, onSuccess]);

  const handlePasswordLogin = useCallback(async () => {
    if (!loginName.trim()) {
      setAuthError('请输入用户名或手机号');
      return;
    }
    if (!password) {
      setAuthError('请输入密码');
      return;
    }
    if (!hasAgreed) {
      setAuthError('请先阅读并同意用户协议和隐私政策');
      return;
    }
    setPwdLoading(true);
    setAuthError(null);
    try {
      await passwordLogin(loginName.trim(), password);
      onSuccess?.();
    } catch (err) {
      setAuthError(err instanceof ApiError ? err.message : '登录失败');
    } finally {
      setPwdLoading(false);
    }
  }, [loginName, password, hasAgreed, onSuccess]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (authMode === 'password') {
        void handlePasswordLogin();
        return;
      }
      if (countdown > 0 && code.length === 6) {
        void handleVerifyLogin();
      } else if (countdown === 0 && phone.length === 11) {
        void handleSendCode();
      }
    }
  };

  // —— 主题变量（2026-10-08 variant 化：page=purple / dialog=emerald 旧弹窗风格）——
  // 2026-10-11 iOS Safari 修复：dialog 输入框字号提到 text-base(16px)——14px 输入框聚焦会触发 Safari 自动放大页面，弹窗错位致按钮点击偏移
  const isDialog = variant === 'dialog';
  const inputCls = isDialog
    ? 'w-full h-12 pl-11 pr-3 bg-[#262626] border border-[#2f2f2f] rounded-xl text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-500/40 focus:ring-1 focus:ring-emerald-500/20 transition-colors'
    : 'w-full h-11 pl-10 pr-3 bg-[#313131] border border-[#333333] rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-colors';
  const primaryBtnCls = isDialog
    ? 'w-full h-12 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2'
    : 'w-full h-11 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 transition-all shadow-lg shadow-purple-500/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2';
  const accentTextCls = isDialog ? 'text-emerald-400 hover:text-emerald-300' : 'text-purple-400 hover:text-purple-300';
  const tabActiveCls = isDialog ? 'bg-emerald-600 text-white' : 'bg-purple-600 text-white';
  const accentIconCls = isDialog ? 'text-emerald-400' : 'text-purple-400';

  return (
    <div className="w-full max-w-md">
      {/* Logo & Title（品牌定制：admin 后台可改，branding 未加载时用默认；dialog 变体用旧弹窗的简洁标题头） */}
      {isDialog ? (
        <div className="mb-7">
          <h1 className="text-xl font-bold text-white tracking-tight">
            {branding?.loginTitle || branding?.name || '深度智像'}
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {authMode === 'sms' ? '未注册的手机号验证后将自动创建账号' : '账号由管理员创建，如需开通请联系管理员'}
          </p>
        </div>
      ) : (
      <div className="text-center mb-7">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-purple-600 mb-3.5 shadow-lg overflow-hidden">
          {branding?.logo ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={branding.logo} alt={branding.name} className="w-9 h-9 object-contain" />
          ) : (
            <Logo size={36} />
          )}
        </div>
        <h1 className="text-2xl font-bold text-white tracking-tight">
          {branding?.loginTitle || branding?.name || '深度智像'}
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          {branding?.loginSubtitle || 'AI 内容生成工作流平台'}
        </p>
      </div>
      )}

      {/* Login Card（dialog 变体透明无卡片——弹窗本身就是容器） */}
      <div className={isDialog ? '' : 'bg-[#1e1e1e]/80 backdrop-blur-xl border border-[#333333]/60 rounded-2xl p-7 shadow-2xl'}>
        {/* 登录方式 Tab（两种均启用时显示切换） */}
        {smsEnabled && passwordEnabled ? (
          <div className={`flex p-1 mb-6 rounded-lg border ${isDialog ? 'bg-[#262626] border-[#2f2f2f]' : 'bg-[#313131] border-[#333333]'}`}>
            <button
              type="button"
              onClick={() => { setAuthMode('sms'); setAuthError(null); }}
              className={`flex-1 h-8 rounded-md text-sm font-medium transition-all ${
                authMode === 'sms' ? tabActiveCls : 'text-slate-400 hover:text-white'
              }`}
            >
              短信登录
            </button>
            <button
              type="button"
              onClick={() => { setAuthMode('password'); setAuthError(null); }}
              className={`flex-1 h-8 rounded-md text-sm font-medium transition-all ${
                authMode === 'password' ? tabActiveCls : 'text-slate-400 hover:text-white'
              }`}
            >
              密码登录
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className={`h-4 w-4 ${accentIconCls}`} />
            <span className="text-sm font-medium text-slate-300">
              {authMode === 'password' ? '账号密码登录' : '手机号登录'}
            </span>
          </div>
        )}

        {/* 账号密码登录表单 */}
        {authMode === 'password' && (
          <div className="space-y-4">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
              <input
                type="text"
                placeholder="用户名或手机号"
                value={loginName}
                onChange={(e) => { setLoginName(e.target.value); setAuthError(null); }}
                onKeyDown={handleKeyDown}
                className={inputCls}
                disabled={pwdLoading}
                autoComplete="username"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
              <input
                type="password"
                placeholder="密码"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setAuthError(null); }}
                onKeyDown={handleKeyDown}
                className={inputCls}
                disabled={pwdLoading}
                autoComplete="current-password"
              />
            </div>

            {authError && (
              <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{authError}</span>
              </div>
            )}

            <button
              onClick={() => void handlePasswordLogin()}
              disabled={pwdLoading}
              className={primaryBtnCls}
            >
              {pwdLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>登录中...</span>
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  <span>登录</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* 手机号 + 验证码登录表单 */}
        {authMode === 'sms' && (
        <div className="space-y-4">
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="请输入手机号"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value.replace(/\D/g, '').slice(0, 11));
                setAuthError(null);
              }}
              onKeyDown={handleKeyDown}
              className={inputCls}
              maxLength={11}
              disabled={smsVerifying}
            />
          </div>

          {/* Code Input + Send Button */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="验证码"
                value={code}
                onChange={(e) => {
                  // iOS 短信验证码一键填充可能带整段短信文案：优先提取连续 6 位数字
                  const raw = e.target.value;
                  const six = raw.match(/\d{6}/);
                  setCode((six ? six[0] : raw.replace(/\D/g, '')).slice(0, 6));
                  setAuthError(null);
                }}
                onKeyDown={handleKeyDown}
                className={`${inputCls} tracking-widest`}
                maxLength={6}
                disabled={smsVerifying}
              />
            </div>
            <button
              onClick={() => void handleSendCode()}
              disabled={smsSending || countdown > 0 || smsVerifying}
              className={`h-12 px-4 rounded-xl text-sm font-medium whitespace-nowrap transition-all flex items-center justify-center min-w-[104px] ${isDialog ? 'border border-[#2f2f2f] bg-[#262626] text-slate-400 hover:text-white hover:border-emerald-500/30' : 'h-11 border border-[#333333] bg-[#313131] text-slate-400 hover:text-white hover:border-purple-500/30'} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {smsSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : countdown > 0 ? (
                `${countdown}s`
              ) : (
                '获取验证码'
              )}
            </button>
          </div>

          {/* Invite Code（邀请活动：默认折叠在验证码下方，aff 链接进入自动展开；仅新用户注册时生效） */}
          {inviteOpen ? (
            <div className="relative">
              <Gift className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
              <input
                type="text"
                placeholder="邀请码（选填）"
                value={inviteCode}
                onChange={(e) => {
                  setInviteCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setAuthError(null);
                }}
                onKeyDown={handleKeyDown}
                className={`${inputCls} pr-10`}
                maxLength={6}
                disabled={smsVerifying}
              />
              <button
                type="button"
                onClick={() => setInviteOpen(false)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-300 transition-colors"
                aria-label="收起邀请码"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className={`flex items-center gap-1.5 text-xs text-slate-500 transition-colors ${isDialog ? 'hover:text-emerald-400' : 'hover:text-purple-400'}`}
            >
              <Gift className="h-3.5 w-3.5" />
              <span>{inviteCode ? `已填邀请码 ${inviteCode}，点击修改` : '填写邀请码（选填）'}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          )}

          {/* Error Message */}
          {authError && (
            <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{authError}</span>
            </div>
          )}

          {/* Login Button（2026-10-11 iOS 修复：不再因输入不完整而 disabled——disabled 按钮点击无任何反馈是"登录没反应"主因；改为可点击+明确错误提示） */}
          <button
            onClick={() => void handleVerifyLogin()}
            disabled={smsVerifying}
            className={primaryBtnCls}
          >
            {smsVerifying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>登录中...</span>
              </>
            ) : (
              <>
                <LogIn className="h-4 w-4" />
                <span>登录 / 注册</span>
              </>
            )}
          </button>
        </div>
        )}

        {/* Footer：协议勾选 + 模式说明 */}
        <div className="mt-5 pt-5 border-t border-[#333333]/60 space-y-3">
          {/* 协议勾选框（登录前必选，未勾选点登录报错） */}
          <div className="flex items-start gap-2">
            <button
              onClick={() => { setHasAgreed(!hasAgreed); setAuthError(null); }}
              className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all ${
                hasAgreed
                  ? isDialog ? 'bg-emerald-600 border-emerald-500' : 'bg-purple-600 border-purple-500'
                  : 'bg-transparent border-slate-500 hover:border-slate-400'
              }`}
              type="button"
              aria-checked={hasAgreed}
              role="checkbox"
            >
              {hasAgreed && (
                <svg viewBox="0 0 12 12" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2.5 6L5 8.5L9.5 3.5" />
                </svg>
              )}
            </button>
            <div className="text-[11px] text-slate-400 leading-relaxed">
              <span>我已阅读并同意</span>
              <a href={`${basePath}/legal/user-agreement`} target="_blank" rel="noopener noreferrer" className={`${accentTextCls} transition-colors underline`}>《用户协议》</a>
              <span>、</span>
              <a href={`${basePath}/legal/privacy-policy`} target="_blank" rel="noopener noreferrer" className={`${accentTextCls} transition-colors underline`}>《隐私政策》</a>
              <span>、</span>
              <a href={`${basePath}/legal/disclaimer`} target="_blank" rel="noopener noreferrer" className={`${accentTextCls} transition-colors underline`}>《免责声明》</a>
              <span>和</span>
              <a href={`${basePath}/legal/community-guidelines`} target="_blank" rel="noopener noreferrer" className={`${accentTextCls} transition-colors underline`}>《社区自律公约》</a>
            </div>
          </div>

          {!isDialog && (
            <p className="text-[11px] text-slate-600 text-center">
              {authMode === 'sms' ? '未注册手机号将自动创建账号' : '账号由管理员创建，如需开通请联系管理员'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
